/**
 * @file ProfileCompleteForm.jsx
 * Component for completing user profile during registration.
 * It collects user's full name, date of birth, password, and phone number.
 * It uses a formik for form handling and validation.
 * It provides UI for entering details and navigating between steps.
 */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import useCompleteProfileForm from './hooks/useCompleteProfileForm';
import { Body2, Body3, H4 } from '../../../themes/typography';
import MuiDatePicker from '../../../components/FormFields/MuiDatePicker';
import '../../../styles/components/LoginForm.css';

export default function ProfileCompleteForm() {
    const { loading, email } = useSelector((state) => state.auth);
    const { getSlideClass, setActiveSlide, showPassword, setShowPassword, formik } =
        useCompleteProfileForm();

    return (
        <React.Fragment>
            <div className="main-login-email-collect-container">
                <div className="login-welcome-greetings">
                    <H4 color="var(--secondary-color)" fontWeight={700}>
                        Complete Profile
                    </H4>
                    <Body2 color="var(--tertiary-color)">Sign up as {email}</Body2>
                </div>

                <form
                    className="main-register-form"
                    onSubmit={formik.handleSubmit}
                    autoComplete="off"
                    noValidate
                >
                    <div
                        className={`different-login-forms slide-transition ${getSlideClass('slide1')}`}
                    >
                        <div className="main-switchable-login-form">
                            <div className="switchable-login-input-container">
                                <TextField
                                    fullWidth
                                    id="name"
                                    name="name"
                                    label="Full name"
                                    type="text"
                                    required
                                    value={formik.values?.name}
                                    onChange={formik.handleChange}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                                <MuiDatePicker
                                    label="Date of Birth"
                                    name="dob"
                                    fullWidth
                                    value={formik.values.dob}
                                    disablePast={false}
                                    disableFuture={true}
                                    onChange={(newValue) => formik.setFieldValue('dob', newValue)}
                                    error={formik.touched.dob && Boolean(formik.errors.dob)}
                                    helperText={formik.touched.dob && formik.errors.dob}
                                />
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        fullWidth
                                        variant="main"
                                        type="submit"
                                        disabled={loading}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Password Login */}
                    <div
                        className={`password-login-method-container slide-transition ${getSlideClass('slide2')}`}
                    >
                        <div className="main-switchable-login-form">
                            <div className="switchable-login-input-container">
                                <TextField
                                    required
                                    fullWidth
                                    id="createPassword"
                                    name="createPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    label="Create password"
                                    value={formik.values.password}
                                    disabled={loading}
                                    onChange={(e) =>
                                        formik.setFieldValue('password', e.target.value)
                                    }
                                    error={
                                        formik.touched.password && Boolean(formik.errors.password)
                                    }
                                    helperText={formik.touched.password && formik.errors.password}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() =>
                                                            setShowPassword(!showPassword)
                                                        }
                                                    >
                                                        {showPassword ? (
                                                            <Visibility color="white" />
                                                        ) : (
                                                            <VisibilityOff color="white" />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }
                                    }}
                                />
                                <TextField
                                    type="text"
                                    required
                                    fullWidth
                                    label="Phone Number"
                                    name="phone"
                                    value={formik.values.phone}
                                    onChange={formik.handleChange}
                                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                                    helperText={formik.touched.phone && formik.errors.phone}
                                    disabled={loading}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    +91
                                                </InputAdornment>
                                            ),
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                            onInput: (e) => {
                                                e.target.value = e.target.value.replace(/\D/g, '');
                                                if (e.target.value.length > 10) {
                                                    e.target.value = e.target.value.slice(0, 10);
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        variant="main"
                                        fullWidth
                                        type="submit"
                                        disabled={loading}
                                        onClick={() =>
                                            formik.setFieldValue('isStepOneCompleted', true)
                                        }
                                    >
                                        Sign In{' '}
                                        {loading && (
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    marginLeft: '0.75rem',
                                                    color: 'var(--primary-color)'
                                                }}
                                            />
                                        )}
                                    </Button>
                                    <Button
                                        disableRipple
                                        sx={backToOption}
                                        disabled={loading}
                                        onClick={() => setActiveSlide('slide1')}
                                    >
                                        Go Back
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                <div className="login-terms-agree-container">
                    <Body3>
                        By sign up in you accept Shmooz {''}
                        <span className="terms">
                            <Link>Terms of Service</Link>
                        </span>{' '}
                        and
                        <span className="terms">
                            {' '}
                            {''}
                            <Link>Privacy Policy</Link>
                        </span>
                    </Body3>
                </div>
            </div>
        </React.Fragment>
    );
}

const backToOption = {
    padding: '0',
    fontSize: '0.875rem',
    color: '#ccc',
    '&:hover': {
        textDecoration: 'underline'
    },
    '&.Mui-disabled': {
        background: 'transparent',
        opacity: 0.6,
        color: 'var(--quaternary-color)'
    }
};
